import type {
  BenefitServiceSummary,
  LimitTypeEnum,
  PaymentFrequencyEnum,
  AdminSubscriberBenefit,
} from "@/api/model";

const renewalCadenceLabelMap: Record<string, string> = {
  weekly: "week",
  every_2_weeks: "2 weeks",
  monthly: "month",
  quarterly: "quarter",
  every_6_months: "6 months",
  annually: "year",
};

export function formatRenewalCadence(
  cadence: string | null | undefined
): string {
  if (!cadence) return "";
  return renewalCadenceLabelMap[cadence] || cadence;
}

export function getRenewalCadenceOptions(): { label: string; value: string }[] {
  return [
    { label: "Weekly", value: "weekly" },
    { label: "Every 2 Weeks", value: "every_2_weeks" },
    { label: "Monthly", value: "monthly" },
    { label: "Quarterly", value: "quarterly" },
    { label: "Every 6 Months", value: "every_6_months" },
    { label: "Annually", value: "annually" },
  ];
}

export function formatLimitType(
  limitType: LimitTypeEnum,
  quantity: number | null,
  frequency?: PaymentFrequencyEnum,
  renewalCadence?: string | null
): string {
  const limitTypeLower = limitType.toLowerCase();

  if (limitTypeLower === "unlimited") {
    return "Unlimited";
  }

  if (limitTypeLower === "fixed") {
    return `${quantity} total`;
  }

  if (limitTypeLower === "recurring") {
    if (renewalCadence) {
      const period = renewalCadenceLabelMap[renewalCadence] || "period";
      return `${quantity} per ${period}`;
    }
    if (frequency) {
      const frequencyLower = frequency.toLowerCase();
      const periodMap: Record<string, string> = {
        weekly: "week",
        monthly: "month",
        annually: "year",
        yearly: "year",
      };
      const period = periodMap[frequencyLower] || "period";
      return `${quantity} per ${period}`;
    }
  }

  return String(quantity || "");
}

export function formatDiscount(percentage: number): string {
  if (percentage >= 100) {
    return "Fully Included";
  }
  return `${percentage}% off`;
}

export function formatBenefitHeader(
  discountPercentage: number,
  limitType: LimitTypeEnum,
  quantity: number | null,
  frequency?: PaymentFrequencyEnum,
  renewalCadence?: string | null
): string {
  return `${formatDiscount(discountPercentage)} (${formatLimitType(
    limitType,
    quantity,
    frequency,
    renewalCadence
  )})`;
}

/**
 * The part of a service summary the UI renders. Summaries are also built locally
 * (from a service picker, or from bare service ids), so they only ever carry the
 * fields we display - not the full API response shape.
 */
export type BenefitServiceDisplaySummary = Pick<
  BenefitServiceSummary,
  "id" | "title"
>;

export interface BenefitServiceDisplaySource {
  readonly services?: readonly string[];
  readonly service_summaries?: readonly BenefitServiceDisplaySummary[];
  readonly service_title?: string;
}

export function getBenefitServiceSummaries(
  benefit: BenefitServiceDisplaySource
): BenefitServiceDisplaySummary[] {
  if (benefit.service_summaries && benefit.service_summaries.length > 0) {
    return benefit.service_summaries.map((service) => ({
      id: service.id,
      title: service.title,
    }));
  }

  if (benefit.service_title) {
    return [
      {
        id: benefit.services?.[0] ?? benefit.service_title,
        title: benefit.service_title,
      },
    ];
  }

  return (benefit.services ?? []).map((serviceId) => ({
    id: serviceId,
    title: serviceId,
  }));
}

export function getBenefitServiceLabel(
  benefit: BenefitServiceDisplaySource
): string {
  const serviceSummaries = getBenefitServiceSummaries(benefit);

  if (serviceSummaries.length === 0) {
    return "-";
  }

  if (serviceSummaries.length === 1) {
    return serviceSummaries[0].title;
  }

  return `${serviceSummaries.length} Services`;
}

export function formatBillingRange(earliest: string, latest: string): string {
  const earliestDate = new Date(earliest);
  const latestDate = new Date(latest);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (earliest === latest) {
    return formatDate(earliestDate);
  }

  const earliestFormatted = earliestDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const latestFormatted = formatDate(latestDate);

  return `${earliestFormatted} - ${latestFormatted}`;
}

export function formatUsage(
  used: number,
  total: number | null,
  limitType: LimitTypeEnum
): string {
  const limitTypeLower = limitType.toLowerCase();

  if (limitTypeLower === "unlimited") {
    return "Unlimited";
  }

  return `${used} of ${total} used`;
}

export function getDiscountPercentageOptions(): {
  label: string;
  value: number;
}[] {
  const options: { label: string; value: number }[] = [];
  for (let i = 5; i <= 100; i += 5) {
    options.push({
      label: formatDiscount(i),
      value: i,
    });
  }
  return options.reverse();
}

export function formatExpiration(expiresAt: string | null): string {
  if (!expiresAt) {
    return "-";
  }

  const now = new Date();
  const expiration = new Date(expiresAt);
  const diffMonths = Math.round(
    (expiration.getTime() - now.getTime()) / (1000 * 60 * 60 * 24 * 30)
  );

  if (diffMonths <= 0) {
    return "Expired";
  }

  if (diffMonths === 1) {
    return "After 1 mo.";
  }

  return `After ${diffMonths} mo.`;
}

export function formatExpirationMonths(
  months: number | null | undefined
): string {
  if (!months) {
    return "-";
  }

  if (months === 1) {
    return "After 1 mo.";
  }

  return `After ${months} mo.`;
}

export function getBenefitStatus(benefit: AdminSubscriberBenefit): string {
  if (benefit.is_frozen) {
    return "Frozen";
  }
  if (benefit.expires_at) {
    const expirationDate = new Date(benefit.expires_at);
    const now = new Date();
    if (expirationDate < now) {
      return "Expired";
    }
  }
  return "Active";
}

export function getBenefitStatusSeverity(
  benefit: AdminSubscriberBenefit
): "success" | "secondary" | "danger" {
  if (benefit.is_frozen) {
    return "secondary";
  }
  if (benefit.expires_at) {
    const expirationDate = new Date(benefit.expires_at);
    const now = new Date();
    if (expirationDate < now) {
      return "danger";
    }
  }
  return "success";
}
